import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createBusyPeriod,
  deleteBusyPeriod,
  fetchBusyPeriodOverlap,
  fetchBusyPeriods,
  updateBusyPeriod,
  UpsertBusyPeriodPayload,
} from "api/busy-period.api";
import { AxiosError } from "axios";
import { fromApiDate, mapBusyPeriodDto, toApiDate } from "pages/out-of-office/busy-period.mapper";
import { SupplierBusyPeriod } from "pages/out-of-office/out-of-office.typing";

export const BUSY_PERIODS_QUERY_KEY = ["busyPeriods"] as const;

export const useBusyPeriodsQuery = (enabled = true) =>
  useQuery<SupplierBusyPeriod[], AxiosError>({
    queryKey: BUSY_PERIODS_QUERY_KEY,
    queryFn: async () => {
      const periods = await fetchBusyPeriods();
      return periods.map(mapBusyPeriodDto);
    },
    enabled,
  });

export const useBusyPeriodOverlapQuery = (
  startDate: Date | undefined,
  endDate: Date | undefined,
  ignoreBusyPeriodId?: string
) =>
  useQuery<Date[], AxiosError>({
    queryKey: [
      ...BUSY_PERIODS_QUERY_KEY,
      "overlap",
      startDate ? toApiDate(startDate) : null,
      endDate ? toApiDate(endDate) : null,
      ignoreBusyPeriodId ?? null,
    ],
    queryFn: async () => {
      const dates = await fetchBusyPeriodOverlap({
        startDate: toApiDate(startDate!),
        endDate: toApiDate(endDate!),
        ignoreBusyPeriodId,
      });
      return dates.map(fromApiDate);
    },
    enabled: Boolean(startDate && endDate),
  });

export const useCreateBusyPeriodMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<SupplierBusyPeriod, AxiosError, UpsertBusyPeriodPayload>({
    mutationFn: async (payload) => mapBusyPeriodDto(await createBusyPeriod(payload)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BUSY_PERIODS_QUERY_KEY });
    },
  });
};

export const useUpdateBusyPeriodMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<SupplierBusyPeriod, AxiosError, { id: string; payload: UpsertBusyPeriodPayload }>({
    mutationFn: async ({ id, payload }) => mapBusyPeriodDto(await updateBusyPeriod(id, payload)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BUSY_PERIODS_QUERY_KEY });
    },
  });
};

export const useDeleteBusyPeriodMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, string>({
    mutationFn: (id) => deleteBusyPeriod(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BUSY_PERIODS_QUERY_KEY });
    },
  });
};
